import { HttpClient, HttpParams } from "@angular/common/http"
import { Injectable, inject } from "@angular/core"
import { Observable, filter, map, switchMap, take } from "rxjs"
import { environment } from "../../../environments/environment"
import { PageResponse } from "../../shared/components/generic-list/table-config"
import { LedgerAccount } from "../models/ledger.model"
import { AuthService } from "./auth.service"

@Injectable({
  providedIn: "root",
})
export class LedgerService {
  private http = inject(HttpClient)
  private authService = inject(AuthService)

  private getApiUrl(): Observable<string> {
    return this.authService.currentUser$.pipe(
      filter((user) => !!user?.account_id),
      take(1),
      map((user) => `${environment.apiUrl}/accounts/${user!.account_id}/ledger`),
    )
  }

  listAccounts(params: HttpParams = new HttpParams()): Observable<PageResponse<LedgerAccount>> {
    return this.getApiUrl().pipe(
      switchMap((apiUrl) => this.http.get<PageResponse<LedgerAccount>>(apiUrl, { params })),
    )
  }

  getAccounts(
    query?: string,
    page: number = 1,
    size: number = 100,
    accountType?: string,
    entityId?: string,
    excludeTypes?: string[],
  ): Observable<LedgerAccount[]> {
    let params = new HttpParams().set("page", page.toString()).set("size", size.toString())

    if (query) {
      params = params.set("q", query)
    }

    if (accountType) {
      params = params.set("account_type", accountType)
    }

    if (excludeTypes) {
      excludeTypes.forEach((t) => {
        params = params.append("exclude_type", t)
      })
    }
    if (entityId) {
      params = params.set("entity_id", entityId)
    }

    return this.getApiUrl().pipe(
      switchMap((apiUrl) => this.http.get<PageResponse<LedgerAccount>>(apiUrl, { params })),
      map((response) => response.items),
    )
  }

  createAccount(account: Partial<LedgerAccount>): Observable<LedgerAccount> {
    return this.getApiUrl().pipe(
      switchMap((apiUrl) => this.http.post<LedgerAccount>(apiUrl, account)),
    )
  }

  updateAccount(id: string, account: Partial<LedgerAccount>): Observable<LedgerAccount> {
    return this.getApiUrl().pipe(
      switchMap((apiUrl) => this.http.patch<LedgerAccount>(`${apiUrl}/${id}`, account)),
    )
  }

  deleteAccount(id: string): Observable<void> {
    return this.getApiUrl().pipe(switchMap((apiUrl) => this.http.delete<void>(`${apiUrl}/${id}`)))
  }

  importAccounts(file: File): Observable<{ created: number; updated: number; message: string }> {
    const formData = new FormData()
    formData.append("file", file)
    return this.getApiUrl().pipe(
      switchMap((apiUrl) =>
        this.http.post<{
          created: number
          updated: number
          message: string
        }>(`${apiUrl}/import`, formData),
      ),
    )
  }
}
